import { useMemo } from "react";
import { useSearchParams } from "react-router";

import {
  EmptystateCodeFileColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { fuzzySearch } from "utils/fuzzySearch";

import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";
import { VersionContext } from "../Context";
import { FILTERS_ORDER_SETTINGS_KEY_VERSION_EXAMPLES, NO_STICKY_RIGHT } from "./constants";
import { MODULE_VERSION_EXAMPLES_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  MODULE_VERSION_EXAMPLES_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
} from "./Table/constants";
import { useSortingModuleVersionExamples } from "./Table/useSortingModuleVersionExamples";
import useModuleVersionExamplesTableStore from "./Table/useTableStore";

const ModuleVersionExamples = () => {
  const { version } = useTypedContext(VersionContext);

  const [searchParams] = useSearchParams();
  const searchQuery = getSearchQuery(searchParams);
  const trackAnalytics = useAnalytics();

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const filteredExamples = useMemo(() => {
    const rows = (version.metadata?.examples || []).map((example) => ({ id: example.name }));

    if (searchQuery) {
      return fuzzySearch(rows, searchQuery.trim(), {
        keys: ["id"],
        scoreThreshold: -1000,
      });
    }

    return rows;
  }, [version.metadata?.examples, searchQuery]);

  const sortedExamples = useSortingModuleVersionExamples(filteredExamples);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Examples"
          tooltip="Example configurations showing how to use this module."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.EXAMPLES}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_EXAMPLES}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {sortedExamples.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={searchQuery ? EmptystateSearchNoResultsColored : EmptystateCodeFileColored}
            caption={
              searchQuery ? "No examples match your search query" : "This module has no examples"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {sortedExamples.length > 0 && (
          <TableSimple
            data={sortedExamples}
            store={useModuleVersionExamplesTableStore}
            ariaLabel="Examples"
            trackAnalytics={trackAnalytics}
            cellPerColumn={MODULE_VERSION_EXAMPLES_TABLE_CELLS}
            filtersStorageKey={MODULE_VERSION_EXAMPLES_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            hideSettings
            stickyRight={NO_STICKY_RIGHT}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionExamples;
